import { useState } from 'react';
import { Calendar, Plus, Trash2, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PlanGate from '../../../../../../../components/gating/PlanGate';
import CustomDatePicker from '../../../../../../../components/forms/CustomDatePicker';
import CustomInput from '../../../../../../../components/forms/CustomInput';
import DayEventsEditor from './DayEventsEditor';

const ItinerarySection = ({
  formData,
  setFormData,
  formVoucherTheme,
  setFormVoucherTheme,
  addItineraryDay,
  predefinedItineraries,
  showAllPredefined,
  setShowAllPredefined,
  matchingPredefinedItineraries,
  applyPredefinedItinerary,
  selectedCityNames,
}) => {
  const [isItineraryExpanded, setIsItineraryExpanded] = useState(false);

  const itineraryDays = formData.itinerary || [];
  const daysCount = itineraryDays.length;

  const handleAddDay = (e) => {
    if (e) e.stopPropagation();
    setIsItineraryExpanded(true);
    addItineraryDay();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm transition-all duration-300" id="itinerary-section">
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4 cursor-pointer select-none group"
        onClick={() => setIsItineraryExpanded(prev => !prev)}
        role="button"
        tabIndex={0}
        aria-expanded={isItineraryExpanded}
        aria-controls="manual-voucher-itinerary-content"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsItineraryExpanded(prev => !prev);
          }
        }}
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner shrink-0 group-hover:bg-teal-100 transition-colors">
            <Calendar size={14} className="sm:w-5 sm:h-5" />
          </div>
          <div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 tracking-wider">Trip Itinerary</h3>
              <span className="px-2 py-0.5 text-[9px] sm:text-xs font-bold bg-teal-50 text-[#067978] rounded-full border border-teal-100">
                {daysCount} {daysCount === 1 ? 'Day' : 'Days'}
              </span>
            </div>
            <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">
              {daysCount} {daysCount === 1 ? 'Day' : 'Days'} · {isItineraryExpanded ? 'Tap to collapse itinerary' : 'Tap to view itinerary'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <PlanGate
            requiredPlan="growth"
            featureName="Include in Voucher PDF toggle"
            tooltipText="Customizing whether the itinerary is included in the voucher PDF requires the Growth Plan."
            showLockIcon={false}
          >
            {({ isLocked, onClick }) => {
              const isChecked = !isLocked && formVoucherTheme.include_itinerary !== false;
              return (
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80">
                  <span className="text-xs font-bold text-slate-700">Include in Voucher PDF</span>
                  <button
                    type="button"
                    onClick={isLocked ? onClick : () => setFormVoucherTheme(prev => ({
                      ...prev,
                      include_itinerary: !(prev.include_itinerary !== false)
                    }))}
                    className={`w-10 h-5 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                      isChecked ? 'bg-primary' : 'bg-slate-200'
                    }`}
                    title={isLocked ? 'Upgrade to Growth Plan to include itinerary in voucher PDF' : undefined}
                  >
                    <span
                      className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                        isChecked ? 'right-0.5' : 'left-0.5'
                      }`}
                    >
                      {isLocked && <Lock size={9} className="text-slate-500" />}
                    </span>
                  </button>
                </div>
              );
            }}
          </PlanGate>

          {isItineraryExpanded ? (
            <>
              <button
                type="button"
                onClick={handleAddDay}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 bg-primary text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-[11px] sm:text-xs hover:opacity-95 transition-all active:scale-95 shadow-sm cursor-pointer"
              >
                <Plus size={14} />
                <span>
                  <span className="sm:hidden">Add</span>
                  <span className="hidden sm:inline">Add Day</span>
                </span>
              </button>
              <div
                className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 hover:bg-teal-50 hover:text-primary transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsItineraryExpanded(false);
                }}
                aria-label="Collapse Itinerary"
                title="Collapse Itinerary"
              >
                <ChevronUp size={20} />
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsItineraryExpanded(true);
              }}
              className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-primary text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-[11px] sm:text-xs hover:opacity-95 transition-all active:scale-95 shadow-sm cursor-pointer"
              aria-label="View Full Itinerary"
            >
              <span>View Full Itinerary</span>
              <ChevronDown size={14} />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isItineraryExpanded && (
          <motion.div
            id="manual-voucher-itinerary-content"
            initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
            animate={{ opacity: 1, height: 'auto', transition: { duration: 0.25, ease: 'easeInOut' } }}
            exit={{ opacity: 0, height: 0, transition: { duration: 0.2, ease: 'easeInOut' } }}
            className="pt-6 border-t border-slate-100 mt-6"
          >
            {formVoucherTheme.include_itinerary === false && (
              <div className="mb-4 px-4 py-2.5 bg-amber-50/80 rounded-xl border border-amber-200/80 flex items-center justify-between gap-2 text-amber-800 text-xs font-medium">
                <span>⚠️ Note: Trip Itinerary entered here is saved in the voucher record, but is currently <strong>hidden from the voucher PDF</strong>.</span>
              </div>
            )}

            {daysCount === 0 ? (
              <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <Calendar size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-xs font-semibold text-slate-600">No itinerary days added yet.</p>
                <p className="text-[11px] text-slate-400 mt-1 mb-4">Add your first day to start structuring sightseeing and schedule.</p>
                <button
                  type="button"
                  onClick={handleAddDay}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl font-bold text-xs hover:opacity-95 cursor-pointer shadow-sm"
                >
                  <Plus size={14} /> Add Day 1
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {itineraryDays.map((day, idx) => {
                  const dayTitle = day.day_title !== undefined ? day.day_title : (day.title !== undefined ? day.title : (day.day || ''));

                  return (
                    <div
                      key={day.id || idx}
                      id={day.id || `itin-${idx}`}
                      className="p-3.5 sm:p-6 bg-white hover:bg-slate-50/20 border border-slate-200 rounded-2xl sm:rounded-3xl relative transition-all duration-300 shadow-sm hover:shadow-md hover:border-slate-300 group mb-4"
                    >
                      {/* Day Top Bar */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 sm:gap-3 mb-3.5 sm:mb-4 pb-3 border-b border-slate-100">
                        {/* Left: Day Label Box + Day Title Input */}
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          <div className="h-[38px] sm:h-[42px] px-2.5 sm:px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold sm:font-normal text-slate-700 shrink-0 flex items-center justify-center select-none shadow-xs">
                            Day {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <CustomInput
                              value={dayTitle}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newItin = [...(formData.itinerary || [])];
                                newItin[idx] = { ...newItin[idx], day_title: val, title: val, day: val };
                                setFormData(prev => ({ ...prev, itinerary: newItin }));
                              }}
                              placeholder="e.g. Arrival in Goa & Beach Tour"
                              inputClassName="font-normal text-slate-700 h-[38px] sm:h-[42px] px-3 sm:px-3.5 text-xs sm:text-sm"
                            />
                          </div>
                        </div>

                        {/* Right: Date Picker + Remove Day */}
                        <div className="flex items-center gap-2 sm:gap-3 w-full md:w-auto shrink-0">
                          <div className="flex-1 min-w-0 md:w-48 lg:w-56 md:shrink-0">
                            <CustomDatePicker
                              value={day.date}
                              onChange={(val) => {
                                const newItin = [...(formData.itinerary || [])];
                                newItin[idx] = { ...newItin[idx], date: val };
                                setFormData(prev => ({ ...prev, itinerary: newItin }));
                              }}
                              placeholder="e.g. 6th June 2026"
                              leftIcon={<Calendar size={14} />}
                              inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-normal text-slate-700 outline-none shadow-sm h-[38px] sm:h-[42px]"
                              className="w-full"
                            />
                          </div>

                          <button
                            onClick={() => setFormData(prev => ({ ...prev, itinerary: prev.itinerary.filter((_, i) => i !== idx) }))}
                            className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors font-bold text-xs shadow-sm cursor-pointer h-[38px] sm:h-[42px] shrink-0"
                            title="Remove Day"
                            type="button"
                          >
                            <Trash2 size={14} /> <span className="hidden sm:inline">Remove</span>
                          </button>
                        </div>
                      </div>

                      {/* Day Events Table */}
                      <div>
                        <DayEventsEditor
                          events={day.events}
                          remarks={day.remarks}
                          timings={day.timings}
                          dayIndex={idx}
                          onChange={(newEvents) => {
                            const newItin = [...(formData.itinerary || [])];
                            newItin[idx] = {
                              ...newItin[idx],
                              events: newEvents,
                              // Keep remarks and timings dual-written for backwards compatibility
                              remarks: newEvents.map(e => e.title || e.activity || '').filter(Boolean),
                              timings: newEvents.map(e => e.time || '')
                            };
                            setFormData(prev => ({
                              ...prev,
                              itinerary_source: prev.itinerary_source || 'manual',
                              itinerary: newItin
                            }));
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ItinerarySection;
